import { useState, useEffect } from "react";
import { X, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Document, Folder, SermonMetadata } from "@/types";
import { get, put } from "@/lib/api";
import { DocumentTagPicker } from "@/components/tags/DocumentTagPicker";
import { useToast } from "@/hooks/use-toast";
import { useReconnect } from "@/hooks/use-reconnect";

interface DocumentSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  document: Document | null;
  sermonMetadata: SermonMetadata | null;
  onUpdateSermonMetadata: (metadata: SermonMetadata) => void;
  onDocumentUpdated?: (doc: Document) => void;
}

export function DocumentSidebar({
  isOpen,
  onClose,
  document,
  sermonMetadata,
  onUpdateSermonMetadata,
  onDocumentUpdated,
}: DocumentSidebarProps) {
  const [metadata, setMetadata] = useState<SermonMetadata | null>(null);
  const [scriptures, setScriptures] = useState<string[]>([]);
  const [newScripture, setNewScripture] = useState("");
  const [folders, setFolders] = useState<Folder[]>([]);
  const [folderId, setFolderId] = useState<string>("none");
  const { toast } = useToast();
  // Re-fetch when the connection returns — see hooks/use-reconnect.
  const reconnectKey = useReconnect();

  useEffect(() => {
    get<Folder[]>("/folders")
      .then(setFolders)
      .catch(() => setFolders([]));
  }, [reconnectKey]);

  useEffect(() => {
    if (sermonMetadata) {
      setMetadata({ ...sermonMetadata, tags: undefined });
      setScriptures(sermonMetadata.scriptures || []);
    } else if (document?.id) {
      setMetadata({
        document_id: document.id,
        scriptures: [],
      });
      setScriptures([]);
    }
    setFolderId(
      document?.folder_id != null ? String(document.folder_id) : "none",
    );
  }, [sermonMetadata, document]);

  const handleSaveMetadata = () => {
    if (metadata) {
      onUpdateSermonMetadata({
        ...metadata,
        scriptures,
        // tags live on document_tags now
        tags: undefined,
      });
    }
  };

  const handleFolderChange = async (value: string) => {
    if (!document) return;
    setFolderId(value);
    const nextFolderId = value === "none" ? null : parseInt(value, 10);
    try {
      const updated = await put<Document>(`/documents/${document.id}`, {
        folder_id: nextFolderId,
      });
      onDocumentUpdated?.(updated);
      toast({ title: "Folder updated" });
    } catch {
      toast({
        title: "Error",
        description: "Failed to move document",
        variant: "destructive",
      });
    }
  };

  const handleAddScripture = () => {
    if (newScripture.trim()) {
      setScriptures([...scriptures, newScripture.trim()]);
      setNewScripture("");
    }
  };

  const handleRemoveScripture = (index: number) => {
    const updated = [...scriptures];
    updated.splice(index, 1);
    setScriptures(updated);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (metadata) {
      setMetadata({
        ...metadata,
        [e.target.name]: e.target.value,
      });
    }
  };

  if (!document) return null;

  const formattedCreatedDate = document.created_at
    ? new Date(document.created_at).toLocaleDateString()
    : "";

  const formattedUpdatedDate = document.updated_at
    ? new Date(document.updated_at).toLocaleDateString()
    : "";

  return (
    <div
      className={`fixed inset-y-0 right-0 z-40 w-80 bg-card border-l border-border transform transition-transform duration-300 ease-in-out overflow-y-auto ${
        isOpen ? "translate-x-0" : "translate-x-full"
      }`}
    >
      <div className="p-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium text-card-foreground">
            Document Details
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <ScrollArea className="pr-4" style={{ height: "calc(100vh - 80px)" }}>
          <div className="space-y-6">
            <Card className="bg-muted/50 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-muted-foreground mb-2">
                Document Info
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Type:</span>
                  <span className="font-medium">{document.doc_type}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Created:</span>
                  <span>{formattedCreatedDate}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Modified:</span>
                  <span>{formattedUpdatedDate}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Word Count:</span>
                  <span>{document.word_count}</span>
                </div>
              </div>
            </Card>

            <div>
              <Label className="mb-2 block">Folder</Label>
              <Select value={folderId} onValueChange={handleFolderChange}>
                <SelectTrigger>
                  <SelectValue placeholder="No folder" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No folder</SelectItem>
                  {folders.map((folder) => (
                    <SelectItem key={folder.id} value={String(folder.id)}>
                      {folder.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="mb-2 block">Tags</Label>
              <DocumentTagPicker documentId={document.id} />
              <p className="text-xs text-muted-foreground mt-2">
                Tip: typing #grace in your draft also links tags on save.
              </p>
            </div>

            {document.doc_type === "Sermon" && metadata && (
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-2">
                  Sermon Details
                </h3>
                <form
                  className="space-y-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSaveMetadata();
                  }}
                >
                  <div>
                    <Label htmlFor="series_name">Series</Label>
                    <Input
                      id="series_name"
                      name="series_name"
                      value={metadata.series_name || ""}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div>
                    <Label htmlFor="delivery_date">Delivery Date</Label>
                    <Input
                      type="date"
                      id="delivery_date"
                      name="delivery_date"
                      value={metadata.delivery_date || ""}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div>
                    <Label htmlFor="audience">Audience</Label>
                    <Input
                      id="audience"
                      name="audience"
                      value={metadata.audience || ""}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div>
                    <Label htmlFor="main_point">Main Point</Label>
                    <Textarea
                      id="main_point"
                      name="main_point"
                      rows={2}
                      value={metadata.main_point || ""}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div>
                    <Label>Scripture References</Label>
                    <div className="mt-1 space-y-2">
                      {scriptures.map((scripture, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <Input value={scripture} readOnly />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground"
                            onClick={() => handleRemoveScripture(index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <div className="flex items-center gap-2">
                        <Input
                          placeholder="e.g. John 3:16"
                          value={newScripture}
                          onChange={(e) => setNewScripture(e.target.value)}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={handleAddScripture}
                          disabled={!newScripture.trim()}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      name="notes"
                      rows={3}
                      value={metadata.notes || ""}
                      onChange={handleInputChange}
                    />
                  </div>

                  <Button type="submit" className="w-full">
                    Save Changes
                  </Button>
                </form>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
